/**
 * Redemptions — the window between a fund exit being agreed and the money moving.
 *
 * A client never calls these. Their way out of a fund is the withdrawal screen;
 * an approved withdrawal of a `private_fund` product enters the redemption
 * machinery on its own (`register_approved_withdrawal`), which is the single
 * money-out path the design records.
 *
 * What these are for is the gap in between: a redemption a client asked for
 * directly sits in `requested` until a manager has approved it AND
 * `redemption_drain` sees `eligible_at` pass; then, in one transaction, the
 * drain posts the ledger debit and the outbox event. Before that moment
 * cancelling costs nothing; after it, undoing means the reversal path.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import { geolocationHeader } from '../lib/geolocation';

export interface RedemptionRequest {
  id: string;
  user_id: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  product_id: string;
  product_name: string;
  /** Null until the drain posts the debit — which is exactly the window this
   *  screen exists for. */
  ledger_entry_id: string | null;
  amount: string;
  currency_code: string;
  reference_number: string;
  status: string;
  /** When the drain may post the debit. Advisory: funds re-verifies the real
   *  term from its own data and fails closed if this ever drifted. */
  eligible_at: string;
  /** When a manager released it to the sweep; null while it waits for one. */
  approved_at: string | null;
  submitted_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  /** What an exit window's gate deferred and still waits for the next window (status `partially_filled`). */
  amount_remaining?: string | null;
  /** A notice of non-renewal: the exit day funds gave it (`yyyy-mm-dd`); null for any other request. */
  exit_on?: string | null;
  /** Whether it can still be cancelled now (a notice withdrawn), by the server's UTC day: the
   *  screens offer it on this, never on the viewer's own calendar. */
  cancellable?: boolean;
  /** The last day a notice of non-renewal can be withdrawn (`yyyy-mm-dd`), as the server keeps it. */
  withdrawable_until?: string | null;
  /** A notice whose withdrawal funds could not apply (the exit had already taken effect there):
   *  it stands again. */
  reinstated?: boolean;
  version: number;
  created_at: string;
}

export interface NonRenewalPayload {
  product_id: string;
  amount: string;
  currency_code: string;
  mfa_code: string;
}

export interface RedemptionListResponse {
  items: RedemptionRequest[];
  total: number;
  page: number;
  size: number;
}

class RedemptionsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  /** The client's notice of non-renewal of a series held to the end of its term: a scheduled
   *  redemption, paid at the end of the current term (funds says the day). Signed with a fresh
   *  authenticator code; precise browser location is a hard gate, as for a withdrawal. */
  async request(payload: NonRenewalPayload): Promise<{ request_id: string }> {
    const headers = { ...(await geolocationHeader()), 'Idempotency-Key': crypto.randomUUID() };
    const res = await this.api.post<{ request_id: string }>(
      '/api/v1/redemptions/request', payload, { headers },
    );
    return res.data;
  }

  /** RBAC-scoped: staff see everything, a client only their own. */
  async list(params: { status?: string[]; page?: number; size?: number } = {}): Promise<RedemptionListResponse> {
    const res = await this.api.get<RedemptionListResponse>('/api/v1/redemptions', {
      params: { status: params.status, page: params.page ?? 1, size: params.size ?? 50 },
      // FastAPI reads a repeated `status` query parameter, not a bracketed one.
      paramsSerializer: { indexes: null },
    });
    return res.data;
  }

  /** A manager releases a redemption to the sweep, signed with their current
   *  authenticator code. Nothing is debited by this call. */
  async approve(requestId: string, expectedVersion: number, mfaCode: string): Promise<void> {
    await this.api.post(`/api/v1/redemptions/${requestId}/approve`, {
      expected_version: expectedVersion,
      mfa_code: mfaCode,
    });
  }

  /** Stop a redemption that has not drained yet. `expected_version` is the
   *  optimistic lock: a stale one is refused rather than overwriting whatever
   *  changed in between. */
  async cancel(requestId: string, expectedVersion: number, reason: string): Promise<void> {
    await this.api.post(`/api/v1/redemptions/${requestId}/cancel`, {
      expected_version: expectedVersion,
      reason: reason || null,
    });
  }
}

export default new RedemptionsService();
